import { useMemo } from 'react';
import { Menu, Bell, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { PanelHeader } from '@reflex/ui/components/layout/PanelHeader';
import { ScrollArea } from '@reflex/ui/components/ui/scroll-area';
import { useActiveOrgSlug } from '@reflex/ui/client/context';
import { withOrgSlug } from '@reflex/shared/routes';
import {
  useDismissNotification,
  useDismissNotifications,
  useNotification,
  useNotifications,
} from '../useNotifications.js';
import { resolveReleaseGroup } from '../releaseGroup.js';
import { NotificationContent } from './NotificationContent';
import { ReleaseGroupContent } from './ReleaseGroupContent';

export interface NotificationDetailPanelProps {
  notificationId: string;
  onToggleSidebar?: () => void;
}

export function NotificationDetailPanel({
  notificationId,
  onToggleSidebar,
}: NotificationDetailPanelProps) {
  const slug = useActiveOrgSlug();
  const { data: notification, isLoading } = useNotification(notificationId);
  const { notifications } = useNotifications();
  const dismiss = useDismissNotification();
  const dismissMany = useDismissNotifications();
  const releaseGroup = useMemo(
    () => resolveReleaseGroup(notification ?? null, notifications),
    [notification, notifications],
  );

  if (isLoading || !notification) {
    return (
      <div className="flex h-full flex-col" data-testid="notification-detail-panel">
        <PanelHeader
          left={
            <>
              {onToggleSidebar && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 md:hidden shrink-0"
                  onClick={onToggleSidebar}
                  aria-label="Open sidebar"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              )}
            </>
          }
        />
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          {isLoading ? 'Loading…' : 'Notification not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="notification-detail-panel">
      <PanelHeader
        left={
          <>
            {onToggleSidebar && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 md:hidden shrink-0"
                onClick={onToggleSidebar}
                aria-label="Open sidebar"
              >
                <Menu className="h-4 w-4" />
              </Button>
            )}
            <Bell className="h-4 w-4 text-muted-foreground" />
            <PanelTitle size="sm">Notification</PanelTitle>
          </>
        }
        right={
          <Link
            to={withOrgSlug(slug, `/notifications/${notification.id}`)}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Open full page
            <ExternalLink className="h-3 w-3" />
          </Link>
        }
      />

      <ScrollArea className="flex-1 min-h-0">
        <div className="px-6 py-8">
          {releaseGroup ? (
            <ReleaseGroupContent
              releases={releaseGroup}
              onDismissAll={() => dismissMany.mutate(releaseGroup.map((r) => r.id))}
              isDismissing={dismissMany.isPending}
              className="mx-auto"
            />
          ) : (
            <NotificationContent
              notification={notification}
              onDismiss={() => dismiss.mutate(notification.id)}
              isDismissing={dismiss.isPending}
              className="mx-auto"
            />
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default NotificationDetailPanel;
